import type { CustomerInput } from '@crm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { PaginationMeta } from '@/modules/auth/types';

export const CUSTOMERS_ROUTES = {
  LIST: '/clientes',
  NEW: '/clientes/novo',
  DETAIL_PATTERN: '/clientes/:id',
  detail: (id: string) => `/clientes/${id}`,
} as const;

const ENDPOINT = '/api/v1/customers';

export interface Customer {
  id: string;
  companyId: string;
  unitId: string | null;
  unitName: string | null;
  name: string;
  image: string | null;
  email: string | null;
  phone: string | null;
  document: string | null;
  birthDate: string | null;
  notes: string | null;
  isFavorite: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CustomersListResponse {
  data: Customer[];
  meta: PaginationMeta;
}

/** Os recortes de clientes que o painel abre. */
export type CustomerSegment =
  | 'new-month'
  | 'new-year'
  | 'returned-quarter'
  | 'not-returned-quarter';

export interface CustomersQuery {
  page: number;
  perPage?: number;
  search?: string;
  unitId?: string;
  status?: 'all' | 'active' | 'inactive';
  favorites?: boolean;
  /** `popular` = quem mais foi atendido primeiro. Padrao da API e por nome. */
  sort?: 'name' | 'popular';
  /** Recorte aberto pelos numeros do painel. */
  segment?: CustomerSegment;
}

export const customersService = {
  list(params: CustomersQuery) {
    const query = new URLSearchParams({
      page: String(params.page),
      status: params.status ?? 'all',
    });

    if (params.perPage) query.set('perPage', String(params.perPage));
    if (params.search) query.set('search', params.search);
    if (params.unitId) query.set('unitId', params.unitId);
    if (params.favorites) query.set('favorites', 'true');
    if (params.sort) query.set('sort', params.sort);
    if (params.segment) query.set('segment', params.segment);

    return apiClient.get<CustomersListResponse>(`${ENDPOINT}?${query.toString()}`);
  },

  get: (id: string) => apiClient.get<Customer>(`${ENDPOINT}/${id}`),
  create: (input: CustomerInput) => apiClient.post<Customer>(ENDPOINT, input),
  update: (id: string, input: Partial<CustomerInput>) =>
    apiClient.patch<Customer>(`${ENDPOINT}/${id}`, input),
  remove: (id: string) => apiClient.delete<{ message: string }>(`${ENDPOINT}/${id}`),
};

const KEYS = {
  all: ['customers'] as const,
  list: (query: CustomersQuery) => ['customers', 'list', query] as const,
  detail: (id: string) => ['customers', 'detail', id] as const,
};

/** `enabled: false` na pagina publica: la o cliente se identifica pelo WhatsApp. */
export function useCustomers(query: CustomersQuery, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: KEYS.list(query),
    queryFn: () => customersService.list(query),
    placeholderData: (previous) => previous,
    enabled: options.enabled ?? true,
  });
}

export function useCustomer(id: string | undefined) {
  return useQuery({
    queryKey: KEYS.detail(id ?? ''),
    queryFn: () => customersService.get(id as string),
    enabled: Boolean(id),
  });
}

function useCustomerMutation<TArgs, TResult = unknown>(
  mutationFn: (args: TArgs) => Promise<TResult>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useCreateCustomer() {
  return useCustomerMutation((input: CustomerInput) => customersService.create(input));
}

export function useUpdateCustomer() {
  return useCustomerMutation(({ id, input }: { id: string; input: Partial<CustomerInput> }) =>
    customersService.update(id, input),
  );
}

export function useDeleteCustomer() {
  return useCustomerMutation((id: string) => customersService.remove(id));
}
